"use client";

import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { ChartContext, type TooltipData } from "../../../context/ChartContext";
import { getYFormatSettings, type YFormatType } from "../../../utils/charts";
import { getAdaptiveTimeFormat } from "../../../utils/timeFormat";

interface ChartTooltipProps {
  data: TooltipData | null;
  formatter: YFormatType;
  /** Optional label to display instead of formatting xValue (for categorical charts) */
  label?: string;
  /** Optional category labels override (for standalone usage without ChartContext) */
  categoryLabels?: Record<string, string>;
}

/**
 * ChartTooltip
 *
 * Interactive tooltip component for charts.
 * Displays formatted data values at hover position with automatic positioning and animations.
 * Uses the main formatting system for consistency with Kpi, StatList, and DataTable.
 *
 * Can be used standalone (with label prop) or within ChartContext (for time-series charts).
 */
export const ChartTooltip: React.FC<ChartTooltipProps> = ({
  data,
  formatter,
  label,
  categoryLabels: categoryLabelsProp,
}) => {
  if (!data) return null;

  // Try to get context values, but don't require them
  const context = React.useContext(ChartContext);
  const categoryLabels = categoryLabelsProp ?? context?.categoryLabels;
  const xScale = context?.xScale;
  const formatSettings = getYFormatSettings(formatter);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState({
    x: 0,
    y: 0,
    translate: "translate(-50%, -100%)",
  });

  React.useEffect(() => {
    if (!tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const rect = tooltip.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const x = data.x;
    let y = data.y;
    let translate = data.position === "left" ? "translate(calc(-100% - 16px), -50%)" : "translate(16px, -50%)";

    // Handle horizontal positioning - check if tooltip would overflow
    if (data.position === "left" && x - rect.width - 16 < 0) {
      // Not enough space on left, position to the right instead
      translate = "translate(16px, -50%)";
    } else if (data.position === "right" && x + rect.width + 16 > viewportWidth) {
      // Not enough space on right, position to the left instead
      translate = "translate(calc(-100% - 16px), -50%)";
    }

    // Handle vertical positioning
    if (y - rect.height / 2 < 0) {
      y = rect.height / 2;
    } else if (y + rect.height / 2 > viewportHeight) {
      y = viewportHeight - rect.height / 2;
    }

    setPosition({ x, y, translate });
  }, [data.x, data.y, data.position]);

  // Get adaptive time format based on the chart's time range (only for temporal domains)
  // If label prop is provided, use it directly (for categorical charts)
  const domain = xScale?.domain();
  const hasTemporalDomain = domain && domain.length === 2 && domain.every((value) => value instanceof Date);
  const timeFormatter = hasTemporalDomain
    ? getAdaptiveTimeFormat({
        start: domain[0] as Date,
        end: domain[1] as Date,
      })
    : null;

  // Format the label based on whether we have a label prop, temporal data, or categorical data
  const formattedLabel = label
    ? label
    : timeFormatter && data.xValue instanceof Date
      ? timeFormatter(data.xValue)
      : String(data.xValue);

  return (
    <AnimatePresence>
      <motion.div
        ref={tooltipRef}
        role="tooltip"
        aria-live="polite"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="tooltip"
        className="pointer-events-none absolute z-50 rounded-lg bg-background-surface p-4 text-sm shadow-xl ring-1 ring-border-default whitespace-nowrap"
        style={{
          left: position.x,
          top: position.y,
          transform: position.translate,
        }}
      >
        <div className="mb-1 font-medium text-text-body">{formattedLabel}</div>

        {data.isEvent ? (
          // Event marker tooltip with series data + event info
          <div className="space-y-2">
            {/* Render underlying series data (all except the last one which is the event) */}
            {data.series.length > 1 && (
              <ul className="list-none p-0 border-b border-border-default pb-2">
                {data.series.slice(0, -1).map((series) => (
                  <li key={series.label} className="py-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-text-body">{series.label}:</span>
                      <span className="text-text-muted">{formatSettings.format(series.value)}</span>
                    </div>
                    {series.category && (
                      <div className="mt-0.5 flex items-center gap-2">
                        <div
                          className="h-3 w-3"
                          style={{
                            backgroundColor: series.categoryColor || series.color,
                            borderRadius: "1px",
                          }}
                        />
                        <span className="text-sm text-text-muted">
                          {categoryLabels?.[series.category] || series.category}
                        </span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* Render event information (last series item) */}
            {(() => {
              const eventSeries = data.series[data.series.length - 1];
              return (
                <div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full" style={{ backgroundColor: eventSeries.color }} />
                    <span className="font-medium text-text-body">{eventSeries.label}</span>
                  </div>
                  {data.eventMetadata && Object.keys(data.eventMetadata).length > 0 && (
                    <div className="ml-4 mt-1 space-y-0.5 text-xs text-text-muted">
                      {Object.entries(data.eventMetadata).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <span className="capitalize">{key}:</span>
                          <span className="font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        ) : (
          // Data point tooltip
          <ul className="list-none p-0">
            {data.series.map((series) => (
              <li key={series.label} className="py-0.5">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-text-body">{series.label}:</span>
                  <span className="text-text-muted">{formatSettings.format(series.value)}</span>
                </div>
                {series.category && (
                  <div className="mt-0.5 flex items-center gap-2">
                    <div
                      className="h-3 w-3"
                      style={{
                        backgroundColor: series.categoryColor || series.color,
                        borderRadius: "1px",
                      }}
                    />
                    <span className="text-sm text-text-muted">
                      {categoryLabels?.[series.category] || series.category}
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
